import { useCallback, useEffect, useRef, useState } from "react";
import { FEATURE_CATALOG, type FeatureFlagKey, type FeatureVisibility } from "@klubora/shared";
import { supabase } from "../lib/supabase";

/**
 * Reads a tenant's `feature_flags` for the platform Features tab — KLUB-119
 * (#324), U10.
 *
 * The CATALOG drives the list, not the table. A tenant provisioned before a
 * feature existed has no row for it, and a missing row resolves to `hidden` in
 * the app. If the screen only listed the rows it found, that feature would be
 * invisible AND unswitchable — so every catalog entry is rendered, and the ones
 * with no row show as `hidden` with a working selector. Saving one creates it.
 *
 * The reverse case (a row for a key no longer in the catalog) is dropped: the
 * app has no gate reading it, so offering the switch would be a lie. The
 * migration already cleared those, but a stale row must not resurrect the UI.
 *
 * Realtime keeps the tab honest when two operators have it open, mirroring
 * `useTenantSettings`.
 */

export interface FeatureFlagState {
  key: FeatureFlagKey;
  label: string;
  visibility: FeatureVisibility;
  comingSoonTitle: string;
  comingSoonMessage: string;
  comingSoonImageUrl: string;
  /** False when the tenant has no row yet — saving creates it. */
  persisted: boolean;
}

interface Row {
  key: string;
  visibility: FeatureVisibility;
  params: Record<string, unknown> | null;
}

function readParam(params: Record<string, unknown> | null, key: string): string {
  const value = params?.[key];
  return typeof value === "string" ? value : "";
}

function buildState(rows: Row[]): FeatureFlagState[] {
  const byKey = new Map(rows.map((r) => [r.key, r]));

  return FEATURE_CATALOG.map((entry) => {
    const row = byKey.get(entry.key);
    return {
      key: entry.key,
      label: entry.label,
      visibility: row?.visibility ?? "hidden",
      comingSoonTitle: readParam(row?.params ?? null, "coming_soon_title"),
      comingSoonMessage: readParam(row?.params ?? null, "coming_soon_message"),
      comingSoonImageUrl: readParam(row?.params ?? null, "coming_soon_image_url"),
      persisted: row !== undefined,
    };
  });
}

/**
 * Trailing-edge window for coalescing realtime events. A save touches one row
 * per changed feature, so a full onboarding pass fires up to seven
 * `postgres_changes` events within milliseconds of each other — without this,
 * each one triggers its own full reload and its own rebuild of the local
 * drafts, which is both wasteful and a chance to clobber whatever the operator
 * typed next.
 */
const REALTIME_COALESCE_MS = 250;

export function useTenantFeatureFlags(tenantId: string | null) {
  // Idle from the first render when there is no tenant to load against, so the
  // hook never writes state from inside an effect for a no-op — mirrors
  // `useTenantSettings`.
  const [flags, setFlags] = useState<FeatureFlagState[]>(() => buildState([]));
  // `loading` is DERIVED, not managed: it is "we have no answer for THIS
  // tenant yet". Tracking it as its own state meant writing it from inside the
  // effect on every tenant change — which the lint rightly flags, and which
  // also left a window where a late-arriving tenantId rendered the empty
  // initial state as if it were loaded.
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loading = tenantId !== null && loadedFor !== tenantId;
  // Read BEFORE every state write, never after an await in the caller — a check
  // that runs once the fetch resolves is theatre, the setState already happened.
  const active = useRef(true);

  // The loader lives INSIDE the effect (mirroring `useTenantSettings`); this
  // ref is the bridge that lets a save re-read without waiting for realtime.
  // Depending on the subscription for the operator's OWN write is what made
  // every successful save appear to revert: `feature_flags` was not in the
  // `supabase_realtime` publication, so the event never arrived and the screen
  // fell back to the stale server list. The publication is fixed too, but the
  // screen should not need it to show what the operator just did.
  const loadRef = useRef<() => Promise<void>>(async () => {});
  const refresh = useCallback(() => loadRef.current(), []);

  useEffect(() => {
    active.current = true;
    if (!tenantId) return () => { active.current = false; };
    let coalesce: ReturnType<typeof setTimeout> | null = null;

    const load = async () => {
      const { data, error: queryError } = await supabase
        .from("feature_flags")
        .select("key, visibility, params")
        .eq("tenant_id", tenantId);
      if (!active.current) return;
      if (queryError) {
        // Do NOT fall back to buildState([]) — every catalog entry would render
        // as `Oculto`, which reads as "this club has everything switched off"
        // rather than "the list did not load", and a click would then seed a
        // draft from that phantom state.
        setError(queryError.message);
        setLoadedFor(tenantId);
        return;
      }
      setError(null);
      setFlags(buildState((data as Row[]) ?? []));
      setLoadedFor(tenantId);
    };
    loadRef.current = load;

    void load();

    const channel = supabase
      .channel(`platform_feature_flags:${tenantId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "feature_flags",
          filter: `tenant_id=eq.${tenantId}`,
        },
        () => {
          if (coalesce) clearTimeout(coalesce);
          coalesce = setTimeout(() => void load(), REALTIME_COALESCE_MS);
        },
      )
      .subscribe((status) => {
        // Realtime is the second operator's channel, not this one's source of
        // truth. Log a broken channel rather than failing silently — without
        // this, a dropped subscription is indistinguishable from "nobody else
        // is editing".
        if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.warn("useTenantFeatureFlags: realtime channel", { status, tenantId });
        }
      });

    return () => {
      active.current = false;
      if (coalesce) clearTimeout(coalesce);
      supabase.removeChannel(channel);
    };
  }, [tenantId]);

  return { flags, loading, error, refresh };
}
